import { useState } from 'react';
import {
  DeleteButton,
  Empty,
  EmployeePicker,
  ErrorBanner,
  Field,
  InlineEdit,
  Loading,
  Modal,
  PageHead,
} from '../components/ui';
import { api } from '../lib/api';
import { formatDate, humanise, todayLocal } from '../lib/format';
import { useResource } from '../lib/hooks';
import { exportApprovalPdf } from './changes/approvalReport';
import type {
  ChangeDecision,
  ChangeImpact,
  ChangeKind,
  ChangePriority,
  ChangeRequest,
  Employee,
} from '../lib/types';

/**
 * Change requests — a change to a process, or a new tool.
 *
 * Two things kept arriving as a message and a conversation: "can we change how
 * we do X" and "can we buy Y". Both need the same handful of answers before
 * anyone can decide, both are decided by one person, and neither is worth a
 * ticketing system. This is that handful of answers, on one page, with a
 * questionnaire to send to whoever signs it off.
 *
 * The form is deliberately SHORT. The change-management canon runs to seven
 * sections — impact analysis, risk register, implementation plan,
 * communication plan, rollback, approvals, sustain. That is right for a
 * regulated deployment and wrong for "should we switch password managers": a
 * form nobody fills in produces no record at all. What is here is what changes
 * a decision.
 */

const KIND: Record<ChangeKind, { label: string; blurb: string }> = {
  process: {
    label: 'Process change',
    blurb: 'Something we do today should be done differently.',
  },
  tool: {
    label: 'New tool',
    blurb: 'We should buy or adopt something we do not have.',
  },
};

const PRIORITIES: ChangePriority[] = ['low', 'medium', 'high', 'critical'];
const IMPACTS: ChangeImpact[] = ['low', 'medium', 'high'];

const DECISIONS: { value: ChangeDecision; label: string }[] = [
  { value: 'approved', label: 'Approved' },
  { value: 'approved_with_conditions', label: 'Approved with conditions' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'declined', label: 'Declined' },
];

/** Crimson only where it means "needs attention" — this palette has no green,
 *  so a low priority is simply not crimson rather than positively reassuring. */
const priorityBadge = (p: ChangePriority): string =>
  p === 'critical' ? 'badge-critical' : p === 'high' ? 'badge-serious' : 'badge-neutral';

const statusBadge = (s: string): string =>
  s === 'approved' ? 'badge-good' : s === 'declined' ? 'badge-critical' : 'badge-neutral';

/* The fields that make a request decidable. Held as data rather than as
   markup so the form, the completeness count and the export cannot drift
   apart — the page can say "3 of 7 answered" only because it knows what the
   seven are. */
const CORE_FIELDS = [
  { key: 'Problem', label: 'The problem', prompt: 'What is wrong today? Write this before the fix.' },
  { key: 'Proposal', label: 'What is proposed', prompt: 'What actually changes.' },
  {
    key: 'Justification',
    label: 'Why it is worth doing',
    prompt: 'Cost, risk, compliance, time saved — with a number if you have one.',
  },
  {
    key: 'WhoIsAffected',
    label: 'Who it affects',
    prompt: 'Name them. "Everyone" is not an impact assessment.',
  },
  {
    key: 'RiskAndMitigation',
    label: 'Risk, and what we would do about it',
    prompt: 'What could go wrong, and the answer to it.',
  },
  {
    key: 'RollbackPlan',
    label: 'How it gets undone',
    prompt: 'The step back out if this does not work. Always have one.',
  },
  {
    key: 'SuccessMeasure',
    label: 'How we will know it worked',
    prompt: 'The number that should move, and by when.',
  },
] as const;

export function ChangeRequestPage() {
  const [scope, setScope] = useState<'open' | 'decided'>('open');
  const requests = useResource<ChangeRequest[]>(`/changes?scope=${scope}`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState<ChangeKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = requests.data ?? [];
  const open = rows.find((r) => r.ChangeRequestId === openId) ?? null;
  const handle = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save');

  const create = (kind: ChangeKind) => {
    setError(null);
    void api
      .post<ChangeRequest>('/changes', { kind, title: kind === 'tool' ? 'New tool' : 'New process change' })
      .then((r) => {
        setCreating(null);
        requests.reload();
        setOpenId(r.ChangeRequestId);
      })
      .catch(handle);
  };

  return (
    <>
      <PageHead
        title="Change Requests"
        lede="A change to how we work, or a tool we do not have yet. One page each — the problem, the proposal, the risk, the way back out — and a questionnaire to send to whoever signs it off."
        actions={
          <>
            <div className="gwc-toggle">
              <button className={scope === 'open' ? 'on-yes' : ''} onClick={() => setScope('open')}>
                Open
              </button>
              <button className={scope === 'decided' ? 'on-yes' : ''} onClick={() => setScope('decided')}>
                Decided
              </button>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setCreating('process')}>
              Raise a request
            </button>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}
      {requests.error && <ErrorBanner message={requests.error} />}
      {requests.loading && <Loading label="Loading change requests" />}

      {!requests.loading && rows.length === 0 && (
        <Empty title={scope === 'open' ? 'Nothing open' : 'Nothing decided yet'}>
          <p className="muted">
            {scope === 'open'
              ? 'Raise one when something should work differently, or when we need a tool we do not have.'
              : 'Approved and declined requests are kept here as the record of what was decided and why.'}
          </p>
        </Empty>
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th style={{ width: 130 }}>Kind</th>
                  <th style={{ width: 110 }}>Priority</th>
                  <th style={{ width: 150 }}>Raised by</th>
                  <th style={{ width: 120 }}>Wanted by</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 90 }}>Ready</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const answered = CORE_FIELDS.filter((f) =>
                    ((r[f.key] as string | null) ?? '').trim(),
                  ).length;
                  return (
                    <tr key={r.ChangeRequestId}>
                      <td>
                        <div className="strong">{r.Title}</div>
                        {r.Kind === 'tool' && r.Vendor && <div className="xs muted">{r.Vendor}</div>}
                      </td>
                      <td className="xs">{KIND[r.Kind].label}</td>
                      <td>
                        <span className={`badge ${priorityBadge(r.Priority)}`}>{humanise(r.Priority)}</span>
                      </td>
                      <td className="xs">{r.RequestedByName ?? <span className="subtle">—</span>}</td>
                      <td className="mono xs">
                        {r.TargetDate ? formatDate(r.TargetDate) : <span className="subtle">—</span>}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge(r.Status)}`}>{humanise(r.Status)}</span>
                      </td>
                      {/* Completeness, not progress: it is the count of the
                          answers an approver needs, so a request can be sent
                          knowing exactly what is missing. */}
                      <td className="mono xs">
                        <span className={answered < CORE_FIELDS.length ? 'flagged' : undefined}>
                          {answered}/{CORE_FIELDS.length}
                        </span>
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setOpenId(r.ChangeRequestId)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <Modal title="Raise a change request" onClose={() => setCreating(null)}>
          <p className="xs muted mb-4">
            Two kinds, one form. A tool request asks four extra questions — the vendor, the cost,
            what it replaces and what data it would hold.
          </p>
          <div className="stack" style={{ gap: 'var(--space-3)' }}>
            {(Object.keys(KIND) as ChangeKind[]).map((k) => (
              <button
                key={k}
                type="button"
                className="mini-row rec-q"
                style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                onClick={() => create(k)}
              >
                <span className="strong">{KIND[k].label}</span>
                <span className="xs muted">{KIND[k].blurb}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {open && (
        <RequestDialog
          request={open}
          employees={employees.data ?? []}
          onClose={() => setOpenId(null)}
          onChanged={() => requests.reload()}
          onDeleted={() => {
            setOpenId(null);
            requests.reload();
          }}
        />
      )}
    </>
  );
}

/* =========================================================================
   One request, on one page
   ========================================================================= */

function RequestDialog({
  request,
  employees,
  onClose,
  onChanged,
  onDeleted,
}: {
  request: ChangeRequest;
  employees: Employee[];
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const r = request;

  const patch = (body: Record<string, unknown>) => {
    setError(null);
    void api
      .put(`/changes/${r.ChangeRequestId}`, body)
      .then(onChanged)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not save'));
  };

  const missing = CORE_FIELDS.filter((f) => !((r[f.key] as string | null) ?? '').trim());

  return (
    <Modal
      title={r.Title}
      onClose={onClose}
      footer={
        <>
          <DeleteButton
            what={`the request "${r.Title}"`}
            onConfirm={() => {
              void api.del(`/changes/${r.ChangeRequestId}`).then(onDeleted);
            }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => exportApprovalPdf(r)}
            title="Opens the print dialog — choose Save as PDF"
          >
            Approval questionnaire
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid mb-4">
        <Field label="Title">
          <input className="input" defaultValue={r.Title} onBlur={(e) => patch({ title: e.target.value })} />
        </Field>
        <Field label="Raised by">
          <EmployeePicker
            employees={employees}
            value={r.RequestedByEmployeeId}
            onChange={(requestedByEmployeeId) => patch({ requestedByEmployeeId })}
            placeholder="Not set"
          />
        </Field>
        <Field label="Priority">
          <select className="select" value={r.Priority} onChange={(e) => patch({ priority: e.target.value })}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {humanise(p)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Impact">
          <select
            className="select"
            value={r.ImpactLevel}
            onChange={(e) => patch({ impactLevel: e.target.value })}
          >
            {IMPACTS.map((p) => (
              <option key={p} value={p}>
                {humanise(p)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Wanted by">
          <input
            className="input"
            type="date"
            defaultValue={r.TargetDate?.slice(0, 10) ?? ''}
            onBlur={(e) => patch({ targetDate: e.target.value || null })}
          />
        </Field>
        <Field label="Status">
          <select className="select" value={r.Status} onChange={(e) => patch({ status: e.target.value })}>
            {['draft', 'submitted', 'approved', 'declined', 'withdrawn'].map((s) => (
              <option key={s} value={s}>
                {humanise(s)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {r.Kind === 'tool' && (
        <section className="mb-4">
          <span className="eyebrow">The tool</span>
          <div className="form-grid">
            <Field label="Tool">
              <input
                className="input"
                defaultValue={r.ToolName ?? ''}
                onBlur={(e) => patch({ toolName: e.target.value || null })}
              />
            </Field>
            <Field label="Vendor">
              <input
                className="input"
                defaultValue={r.Vendor ?? ''}
                onBlur={(e) => patch({ vendor: e.target.value || null })}
              />
            </Field>
            <Field label="Cost a year" hint="Annual, so it is compared against the right number.">
              <input
                className="input mono"
                type="number"
                min={0}
                step="0.01"
                defaultValue={r.AnnualCost ?? ''}
                onBlur={(e) =>
                  patch({ annualCost: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
            </Field>
            <Field label="What it replaces" hint="Blank means it is additive — which is itself an answer.">
              <input
                className="input"
                defaultValue={r.Replaces ?? ''}
                onBlur={(e) => patch({ replaces: e.target.value || null })}
              />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Data it would hold">
              <InlineEdit
                value={r.DataHandled}
                rows={2}
                placeholder="Client or staff data it would touch — this is what decides whether Compliance needs to see it."
                onSave={(dataHandled) => patch({ dataHandled })}
              />
            </Field>
          </div>
        </section>
      )}

      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {CORE_FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            <InlineEdit
              value={r[f.key] as string | null}
              rows={3}
              placeholder={f.prompt}
              onSave={(v) => patch({ [f.key.charAt(0).toLowerCase() + f.key.slice(1)]: v })}
            />
          </Field>
        ))}
      </div>

      {missing.length > 0 && (
        <p className="risk-note xs mt-4">
          <strong>{missing.length} still unanswered: </strong>
          {missing.map((f) => f.label.toLowerCase()).join(', ')}. The questionnaire prints these as
          gaps rather than hiding them — an approver should see what nobody has said yet.
        </p>
      )}

      {/* --- the decision, when it comes back ------------------------------ */}
      <section className="mt-6">
        <span className="eyebrow">The decision</span>
        <div className="form-grid">
          <Field label="Outcome">
            <select
              className="select"
              value={r.Decision ?? ''}
              onChange={(e) => {
                const decision = e.target.value || null;
                /* Recording an outcome is what closes a request — leaving the
                   status behind would let a decided one sit in the open list
                   forever. Deferred stays open, because it is not decided. */
                patch({
                  decision,
                  decidedOn: r.DecidedOn ?? (decision ? todayLocal() : null),
                  status:
                    decision === 'declined'
                      ? 'declined'
                      : decision === 'approved' || decision === 'approved_with_conditions'
                        ? 'approved'
                        : r.Status,
                });
              }}
            >
              <option value="">Not yet decided</option>
              {DECISIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Decided by">
            <EmployeePicker
              employees={employees}
              value={r.DecidedByEmployeeId}
              onChange={(decidedByEmployeeId) => patch({ decidedByEmployeeId })}
              placeholder="Not set"
            />
          </Field>
          <Field label="Decided on">
            <input
              className="input"
              type="date"
              defaultValue={r.DecidedOn?.slice(0, 10) ?? ''}
              onBlur={(e) => patch({ decidedOn: e.target.value || null })}
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Conditions" hint="“Approved, but…” is the most common real answer and the one most often lost.">
            <InlineEdit
              value={r.Conditions}
              rows={2}
              placeholder="What the approval depends on."
              onSave={(conditions) => patch({ conditions })}
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Note">
            <InlineEdit
              value={r.DecisionNote}
              rows={2}
              placeholder="Anything said at the time that would be worth reading in six months."
              onSave={(decisionNote) => patch({ decisionNote })}
            />
          </Field>
        </div>
      </section>
    </Modal>
  );
}
